import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { authFetch } from "../utils/authFetch";
import { BsCheckCircleFill, BsExclamationTriangleFill } from "react-icons/bs";

export default function Bilant() {
  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, 0, 1));
  const [to, setTo] = useState(today);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const formatDateString = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const res = await authFetch(`/rapoarte/bilant?from=${fromStr}&to=${toStr}`);
      if (res && res.active && res.pasive) {
        setData(res);
      } else {
        setData(null);
        setErr("Format răspuns invalid de la server.");
      }
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare bilanț contabil.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt2 = (n) => {
    const val = Number(n ?? 0);
    if (Math.abs(val) < 0.005) return "0,00";
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const active = data?.active || {};
  const pasive = data?.pasive || {};

  return (
    <div className="p-3">
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
      
      <Card className="shadow-sm border-0 rounded-4 p-4 print-container">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 no-print">
          <div>
            <h4 className="fw-bold text-dark mb-1">Bilanț Contabil (Situație Patrimonială)</h4>
            <p className="text-muted small mb-0">
              Vizualizează activele, datoriile și capitalurile proprii ale societății la data selectată.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={handlePrint} className="rounded-3 fw-semibold">
              Printează
            </Button>
            <Button variant="primary" onClick={load} disabled={busy} className="rounded-3 fw-semibold">
              {busy ? "Se încarcă..." : "Generează"}
            </Button>
          </div>
        </div>

        {/* Date pickers (no-print) */}
        <Row className="g-3 mb-4 align-items-end no-print">
          <Col xs={12} sm={8} md={6} lg={4}>
            <label className="fw-semibold text-secondary small mb-1 d-block">Perioada de analiză</label>
            <div className="d-flex align-items-center gap-2">
              <DatePicker
                selected={from}
                onChange={(date) => setFrom(date)}
                dateFormat="dd.MM.yyyy"
                className="form-control rounded-3 border-light-subtle shadow-none"
              />
              <span className="text-secondary">-</span>
              <DatePicker
                selected={to}
                onChange={(date) => setTo(date)}
                dateFormat="dd.MM.yyyy"
                className="form-control rounded-3 border-light-subtle shadow-none"
              />
            </div>
          </Col>
        </Row>

        {err && (
          <Alert variant="danger" className="rounded-3 mb-4 no-print">
            {err}
          </Alert>
        )}

        {/* Correlation Badge */}
        {data && (
          <div className="mb-4">
            {data.corespondenta_ok ? (
              <Alert variant="success" className="d-flex align-items-center gap-2 rounded-4 py-2 border-0 bg-success-subtle text-success-emphasis mb-0">
                <BsCheckCircleFill className="fs-5" />
                <div className="small">
                  <strong>Echilibru patrimonial valid:</strong> Total Activ este egal cu Total Pasiv ({fmt2(active.total_activ)} RON).
                </div>
              </Alert>
            ) : (
              <Alert variant="danger" className="d-flex align-items-center gap-2 rounded-4 py-2 border-0 bg-danger-subtle text-danger-emphasis mb-0">
                <BsExclamationTriangleFill className="fs-5" />
                <div className="small">
                  <strong>Atenție, dezechilibru patrimonial:</strong> Diferența dintre Activ și Pasiv este de <strong>{fmt2(data.diferenta)} RON</strong>. (Activ: {fmt2(active.total_activ)} / Pasiv: {fmt2(pasive.total_pasiv)}).
                </div>
              </Alert>
            )}
          </div>
        )}

        {busy ? (
          <div className="py-5 text-center">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2 mb-0">Se calculează situația patrimonială...</p>
          </div>
        ) : data ? (
          <Row className="g-4">
            {/* COLUMN 1: ACTIV */}
            <Col xs={12} md={6}>
              <div className="rounded-3 border border-light-subtle overflow-hidden">
                <Table bordered hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr className="bg-primary text-white text-center">
                      <th className="py-2 bg-primary text-white border-0" style={{ width: "70%" }}>ACTIV</th>
                      <th className="py-2 bg-primary text-white border-0 text-end">Suma (RON)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold text-secondary bg-light" colSpan="2">A. ACTIVE IMOBILIZATE</td>
                    </tr>
                    <tr>
                      <td className="ps-4">I. Imobilizări necorporale</td>
                      <td className="text-end">{fmt2(active.imobilizari_necorporale)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">II. Imobilizări corporale</td>
                      <td className="text-end">{fmt2(active.imobilizari_corporale)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">III. Imobilizări financiare</td>
                      <td className="text-end">{fmt2(active.imobilizari_financiare)}</td>
                    </tr>
                    <tr className="table-secondary-subtle fw-bold text-primary">
                      <td className="ps-3">TOTAL ACTIVE IMOBILIZATE</td>
                      <td className="text-end">{fmt2(active.total_imobilizari)}</td>
                    </tr>

                    <tr>
                      <td className="fw-semibold text-secondary bg-light" colSpan="2">B. ACTIVE CIRCULANTE</td>
                    </tr>
                    <tr>
                      <td className="ps-4">I. Stocuri</td>
                      <td className="text-end">{fmt2(active.stocuri)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">II. Creanțe</td>
                      <td className="text-end">{fmt2(active.creante)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">III. Investiții pe termen scurt</td>
                      <td className="text-end">{fmt2(active.investitii_scurte)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">IV. Casa și conturi la bănci</td>
                      <td className="text-end">{fmt2(active.casa_banci)}</td>
                    </tr>
                    <tr className="table-secondary-subtle fw-bold text-primary">
                      <td className="ps-3">TOTAL ACTIVE CIRCULANTE</td>
                      <td className="text-end">{fmt2(active.total_circulante)}</td>
                    </tr>

                    <tr>
                      <td className="fw-semibold text-secondary bg-light" colSpan="2">C. CHELTUIELI ÎN AVANS</td>
                    </tr>
                    <tr>
                      <td className="ps-4">Cheltuieli înregistrate în avans (cont 471)</td>
                      <td className="text-end">{fmt2(active.cheltuieli_avans)}</td>
                    </tr>

                    <tr className="table-primary fw-bold fs-6 text-primary border-top border-primary">
                      <td className="py-2">TOTAL ACTIV (A + B + C)</td>
                      <td className="text-end py-2">{fmt2(active.total_activ)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Col>

            {/* COLUMN 2: PASIV */}
            <Col xs={12} md={6}>
              <div className="rounded-3 border border-light-subtle overflow-hidden">
                <Table bordered hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr className="bg-primary text-white text-center">
                      <th className="py-2 bg-primary text-white border-0" style={{ width: "70%" }}>PASIV ȘI CAPITALURI</th>
                      <th className="py-2 bg-primary text-white border-0 text-end">Suma (RON)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold text-secondary bg-light" colSpan="2">A. DATORII</td>
                    </tr>
                    <tr>
                      <td className="ps-4">Datorii comerciale, fiscale, salariale, credite bancare</td>
                      <td className="text-end">{fmt2(pasive.datorii)}</td>
                    </tr>
                    
                    <tr>
                      <td className="fw-semibold text-secondary bg-light" colSpan="2">B. VENITURI ÎN AVANS</td>
                    </tr>
                    <tr>
                      <td className="ps-4">Venituri înregistrate în avans, subvenții (cont 472, 475)</td>
                      <td className="text-end">{fmt2(pasive.venituri_avans)}</td>
                    </tr>

                    <tr>
                      <td className="fw-semibold text-secondary bg-light" colSpan="2">C. PROVIZIOANE</td>
                    </tr>
                    <tr>
                      <td className="ps-4">Provizioane pentru riscuri și cheltuieli (cont 151)</td>
                      <td className="text-end">{fmt2(pasive.provizioane)}</td>
                    </tr>

                    <tr>
                      <td className="fw-semibold text-secondary bg-light" colSpan="2">D. CAPITALURI PROPRII</td>
                    </tr>
                    <tr>
                      <td className="ps-4">I. Capital subscris vărsat (cont 101)</td>
                      <td className="text-end">{fmt2(pasive.capital_subscris)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">II. Prime de capital (cont 104)</td>
                      <td className="text-end">{fmt2(pasive.prime_capital)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">III. Rezerve din reevaluare (cont 105)</td>
                      <td className="text-end">{fmt2(pasive.rezerve_reevaluare)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">IV. Rezerve (cont 106)</td>
                      <td className="text-end">{fmt2(pasive.rezerve)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">V. Acțiuni proprii (cont 109, scăzute)</td>
                      <td className="text-end">{fmt2(pasive.actiuni_proprii)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">VI. Rezultatul reportat (cont 117)</td>
                      <td className="text-end">{fmt2(pasive.rezultat_reportat)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">VII. Rezultatul exercițiului (cont 121, profit/pierdere)</td>
                      <td className="text-end">{fmt2(pasive.rezultat_exercitiu)}</td>
                    </tr>
                    <tr>
                      <td className="ps-4">VIII. Repartizarea profitului (cont 129, scăzută)</td>
                      <td className="text-end">{fmt2(pasive.repartizare_profit)}</td>
                    </tr>
                    <tr className="table-secondary-subtle fw-bold text-primary">
                      <td className="ps-3">TOTAL CAPITALURI PROPRII</td>
                      <td className="text-end">{fmt2(pasive.total_capitaluri)}</td>
                    </tr>

                    <tr className="table-primary fw-bold fs-6 text-primary border-top border-primary">
                      <td className="py-2">TOTAL PASIV (A + B + C + D)</td>
                      <td className="text-end py-2">{fmt2(pasive.total_pasiv)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        ) : (
          <div className="py-4 text-center text-muted">
            Apăsați pe "Generează" pentru a încărca datele bilanțului.
          </div>
        )}
      </Card>
    </div>
  );
}
